/**
 * The Record's doors (D241) — where the store keeps a player's Record with
 * their account, and how `play-api` and the browser reach it.
 *
 * @remarks
 * Mike's ruling (2026-10-09): the Record is held with the player's ACCOUNT,
 * so it follows them across devices and a paid run — played on the server —
 * reads it where the run is played. A free player who is not signed in keeps
 * it on the DEVICE, and it merges into the account the first time they sign
 * in (buying needs an account, so a purchase is a sign-in first).
 *
 * The browser's doors, with the identity cookie or bearer session:
 * - `GET /record?gameId=` — {@link recordAnswerSchema} out: this account's
 *   Record for the game (a client-run game reads it at genesis).
 * - `POST /record` — {@link recordAppendRequestSchema} in, {@link recordAnswerSchema}
 *   out: fold layers in — the layers a client-run turn wrote, or the whole
 *   of a device's Record merging into the account.
 * - `POST /record/forget` — {@link recordForgetRequestSchema} in: "forget the
 *   Record" (Calder's ruling) deletes this account's Record for the game.
 *
 * `play-api`'s, over the service binding, with the internal header:
 * - `GET /internal/record?userId=&gameId=` — {@link recordAnswerSchema} out:
 *   read at a signed-in player's genesis.
 * - `POST /internal/record` — {@link internalRecordAppendRequestSchema} in:
 *   the layers a server-run turn wrote, kept for the account.
 * - `POST /internal/record/forget` — {@link internalRecordForgetRequestSchema}
 *   in (D242): "Forget the Record" for a server-run game, asked by `play-api`
 *   for the signed-in player it resolved.
 *
 * And `play-api`'s own, for the browser (D242):
 * - `POST /record/forget` ({@link PLAY_RECORD_FORGET_PATH}) —
 *   {@link recordForgetRequestSchema} in, {@link recordAnswerSchema} out: a
 *   server-run game's "Forget the Record". The play Worker is the one that
 *   reads a signed-in player's Record for such a game, so it is the one that
 *   forgets it — whether or not the build names a store Worker the browser
 *   could reach. Signed out, there is nothing held server-side to forget,
 *   and the answer is the same empty Record.
 *
 * Every layer is checked against the shared shape and nothing else: the
 * store cannot know a pack's `record`, and does not need to — genesis
 * keeps only what the pack it is played on remembers.
 *
 * @packageDocumentation
 */

import { gameIdSchema, recordLayersSchema } from '@orpheus/content-schema';
import { z } from 'zod';

/** The browser's door to the account's Record (`GET` to read, `POST` to fold layers in). */
export const STORE_RECORD_PATH = '/record';

/** The browser's door to forget the account's Record for one game. */
export const STORE_RECORD_FORGET_PATH = '/record/forget';

/** `play-api`'s door to an account's Record, over the binding. */
export const STORE_INTERNAL_RECORD_PATH = '/internal/record';

/** D242: `play-api`'s door to forget an account's Record for one game, over the binding. */
export const STORE_INTERNAL_RECORD_FORGET_PATH = '/internal/record/forget';

/** D242: the play Worker's door for a server-run game's "Forget the Record" — the browser's, with the cookie along. */
export const PLAY_RECORD_FORGET_PATH = '/record/forget';

/** One account's Record for one game, newest layer last. */
export const recordAnswerSchema = z.strictObject({ layers: recordLayersSchema }).readonly();

export type RecordAnswer = z.infer<typeof recordAnswerSchema>;

/** Fold layers into the signed-in account's Record for a game. */
export const recordAppendRequestSchema = z
	.strictObject({
		gameId: gameIdSchema,
		layers: recordLayersSchema,
	})
	.readonly();

export type RecordAppendRequest = z.infer<typeof recordAppendRequestSchema>;

/** Forget the signed-in account's Record for a game. */
export const recordForgetRequestSchema = z.strictObject({ gameId: gameIdSchema }).readonly();

export type RecordForgetRequest = z.infer<typeof recordForgetRequestSchema>;

/** `play-api`'s fold: the layers a server-run turn wrote, for the account that played it. */
export const internalRecordAppendRequestSchema = z
	.strictObject({
		gameId: gameIdSchema,
		layers: recordLayersSchema,
		userId: z.string().min(1),
	})
	.readonly();

export type InternalRecordAppendRequest = z.infer<typeof internalRecordAppendRequestSchema>;

/** D242: `play-api`'s forget: one game's Record, for the account the play Worker resolved. */
export const internalRecordForgetRequestSchema = z
	.strictObject({
		gameId: gameIdSchema,
		userId: z.string().min(1),
	})
	.readonly();

export type InternalRecordForgetRequest = z.infer<typeof internalRecordForgetRequestSchema>;
